import { getAllISOCodes } from 'iso-country-currency';
import moment from 'moment-timezone';

import { importLocaleModule, isMomentLocaleLoadable } from '~/components/CmsMain/localeGlobals/importLocaleModule';
import { getCurrencyFromCountry } from '~/Utils/currencyUtils';

export const DEFAULT_LOCALE = 'en-us';

export let localeDetails = null;

// iso-country-currency has no entry for every region, and a locale on one has no currency to format
// money with. Enumerating the mapped regions up front is what lets the organization settings screen
// refuse to offer such a locale in the first place, rather than storing it and leaving the currency
// side of `localeDetails` empty for every screen that reads it.
const REGIONS_WITH_CURRENCY = new Set(
  getAllISOCodes()
    .filter(({ currency }) => currency)
    .map(({ iso }) => iso)
);

/**
 * Whether an organization can safely be configured with this locale.
 *
 * The two capabilities that live in frontend packages: a currency mapping for the locale's region,
 * and moment locale data. The other two — a `holidays` calendar for the territory and a CLDR
 * language for it — are what the server-derived locale list already guarantees, so the settings
 * screen filters that list through here rather than assembling a list of its own.
 */
export const isSupportedOrganizationLocale = (localeCode) => {
  if (!localeCode) {
    return false;
  }

  let region;
  try {
    region = new Intl.Locale(localeCode).region;
  } catch {
    return false;
  }

  return Boolean(region) && REGIONS_WITH_CURRENCY.has(region) && isMomentLocaleLoadable(localeCode);
};

export const updateOrganizationLocaleDetails = async (organization, user) => {
  await updateLocaleDetails(organization.configuration.locale?.toLowerCase(), user?.locale?.toLowerCase());
};

export const updateLocaleDetails = async (orgLocale = DEFAULT_LOCALE, userLocale) => {
  let locale_obj = new Intl.Locale(orgLocale);
  // Through the shared helper rather than iso-country-currency directly: the package is unmaintained
  // (0.7.2, April 2023) and lags real currency changes, so `getCurrencyFromCountry` is where the
  // product corrects it — Bulgaria's Jan 2026 Euro adoption is carried by no release of the package.
  // Reading the package here too would give a Bulgarian organization BGN in every money format while
  // the policy dialog beside it offered EUR.
  localeDetails = { locale: locale_obj, currency: getCurrencyFromCountry(locale_obj.region) };
  await loadLocale(userLocale || orgLocale);
};

const loadLocale = async (localeCode) => {
  const lowerCaseLocaleCode = localeCode.toLowerCase();
  try {
    if (!['en-us'].includes(lowerCaseLocaleCode)) {
      await importLocaleModule(lowerCaseLocaleCode);
      moment.locale(lowerCaseLocaleCode);
    } else {
      moment.locale(lowerCaseLocaleCode);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to load locale: ${lowerCaseLocaleCode}`, error);
  }
};
